export type Unit = "seconds" | "milliseconds";

/** Seconds vs milliseconds by magnitude: >= 1e11 is read as ms (year 5138 in s, 1973 in ms). */
export function detectUnit(n: number): Unit {
  return Math.abs(n) >= 1e11 ? "milliseconds" : "seconds";
}

export type ParsedTs = { ok: true; ms: number; unit: Unit } | { ok: false; error: string };

export function parseTimestamp(raw: string): ParsedTs {
  const v = raw.trim();
  if (v === "") return { ok: false, error: "" };
  if (!/^-?\d+(\.\d+)?$/.test(v)) return { ok: false, error: "Not a number" };
  const n = Number(v);
  const unit = detectUnit(n);
  const ms = unit === "seconds" ? n * 1000 : n;
  if (!Number.isFinite(ms) || Math.abs(ms) > 8.64e15) return { ok: false, error: "Out of range" };
  return { ok: true, ms, unit };
}

/** Parses "YYYY-MM-DDTHH:mm[:ss]" as UTC, never as local time. Returns ms or null. */
export function parseUtcDateTime(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, s ? +s : 0);
  return Number.isNaN(ms) ? null : ms;
}

export function toUtcInputValue(ms: number): string {
  return new Date(ms).toISOString().slice(0, 19);
}

export function relativeTime(ms: number, now = Date.now()): string {
  const diff = ms - now;
  const abs = Math.abs(diff);
  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [31557600000, "year"],
    [2629800000, "month"],
    [86400000, "day"],
    [3600000, "hour"],
    [60000, "minute"],
    [1000, "second"],
  ];
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  for (const [size, unit] of units) {
    if (abs >= size || unit === "second") return rtf.format(Math.round(diff / size), unit);
  }
  return "now";
}
